import React, { useState } from 'react';
import zxcvbn from 'zxcvbn';
import { errors } from '../../../services/errors';

import { session } from '../../../services/session';
import { authService } from '../../../services/auth';
import { camelCaseToDash, titleCase } from '../common/utils';

import SnackBar from '../../admin/accounts/snackbar';
import Strength from '../authentication/strength';

import './general-settings.scss';

const GeneralSettings = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [snackbarTxt, setSnackbarTxt] = useState('');

  const userInfo = ['firstName', 'lastName', 'email'].map((attribute, index) => {
    const className = camelCaseToDash(attribute);
    const formattedValue =
      attribute !== 'email' ? titleCase(session[attribute]) : session[attribute];
    return (
      <li key={index} className={className}>
        <span className="label">{titleCase(className.replace(/-/g, ' '))}</span>
        <span className="value">{formattedValue}</span>
      </li>
    );
  });

  function handleNewPasswordChange(value) {
    setPasswordStrength(zxcvbn(value).score);
    setNewPassword(value);
  }

  function getPasswordItems() {
    const passwordMap = [
      {
        label: 'password',
        value: oldPassword,
        setValue: setOldPassword,
      },
      {
        label: 'new password',
        value: newPassword,
        setValue: handleNewPasswordChange,
      },
      {
        label: 'confirm new password',
        value: repeatNewPassword,
        setValue: setRepeatNewPassword,
      },
    ];

    const passwordItems = Object.values(passwordMap).map((item, index) => {
      const className = camelCaseToDash(item.label);
      const strength =
        index === 1 && newPassword && !snackbarTxt ? (
          <Strength score={newPassword.length < 8 ? 'short' : passwordStrength} />
        ) : null;

      return (
        <li key={index} className={className}>
          <span className="label">{titleCase(item.label)}</span>
          <input value={item.value} onChange={e => item.setValue(e.target.value)} type="password" />
          {strength}
        </li>
      );
    });

    return passwordItems;
  }

  function getCredentials() {
    return {
      username: session.userName,
      old_password: oldPassword,
      new_password: newPassword,
      repeat_new_password: repeatNewPassword,
    };
  }

  function validCredentials(limited = false) {
    /*
     * Limited attribute - for limited validation, to determine
     * if the submit button should be greyed out
     */
    const credentials = getCredentials();
    const fields = Object.keys(credentials);

    fields.forEach((field, index) => {
      if (!credentials[fields[index]]) {
        if (!limited) setValidationMessage(errors.messages.credentials_empty);
      }
    });

    if (limited) return true;

    if (!passwordsMatch()) {
      setValidationMessage(errors.messages.password_not_match);
      return false;
    }
    if (credentials.new_password.length < 8) {
      setValidationMessage(errors.messages.password_too_short);
      return false;
    }
    if (credentials.new_password.length > 70) {
      setValidationMessage(errors.messages.password_too_long);
      return false;
    }

    return true;
  }

  function handleError(error) {
    let message = '';
    const errorCode = error.code;

    switch (errorCode) {
      case 4001:
        message = errors.messages.password_not_match;
        break;
      case 4002:
        message = errors.messages.password_too_short;
        break;
      case 4003:
        message = errors.messages.password_same;
        break;
      case 4301:
        message = errors.messages.credentials_current_incorrect;
        break;
      default:
        message = error.message;
        break;
    }

    setValidationMessage(message);
  }

  function passwordsMatch() {
    const credentials = getCredentials();
    return (
      credentials.new_password === credentials.repeat_new_password &&
      credentials.new_password !== ''
    );
  }

  function updatePassword() {
    if (!validCredentials()) return;
    const credentials = getCredentials();
    authService
      .changePassword(credentials)
      .then(() => {
        setSnackbarTxt('Password Changed.');
        setValidationMessage('');
        return authService.login({
          username: credentials.username,
          password: credentials.new_password,
        });
      })
      .catch(e => handleError(e));
  }

  const snackBar = snackbarTxt ? (
    <SnackBar message={snackbarTxt} setSnackbarTxt={setSnackbarTxt} />
  ) : null;

  return (
    <div className="general-settings">
      <h3>Contact</h3>
      {userInfo}
      <hr></hr>
      <h3>Change Password</h3>
      <div className="password-items">{getPasswordItems()}</div>
      <hr></hr>
      <p className="warning">{validationMessage}</p>
      <button className="submit-btn" onClick={updatePassword}>
        Update Account Details
      </button>
      {snackBar}
    </div>
  );
};

export default GeneralSettings;
